import {Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild} from '@angular/core';
import {
  LMListComponent,
  LMListRowActivationEvent,
  LMListSelectionEvent,
  LMListTransactionEvent,
} from '@list/lm-list.component';
import {AppConstants} from '@utils/app.constants';
import {Observable, of} from 'rxjs';
import {LMUrlInfoBuilderService} from '@services/lm-url-info-builder.service';
import {LMRefreshRequest} from "@app/components";

export class LMPaneListSelectionEvent {
  constructor(
    public listComponent: LMListComponent,
    public selectedRow: LMListRowData,
    public pagePaneListComponent: LMPagePaneListComponent
  ) {}
}

export class LMPaneRowActivatedEvent {
  constructor(
    public listComponent: LMListComponent,
    public activatedEvent: LMListRowActivatedEvent,
    public pagePaneListComponent: LMPagePaneListComponent
  ) {}
}

export class LMPaneListSendContextMessageEvent {
  constructor(
    public contextMsgs: any,
    public listComponent: LMListComponent,
    public pagePaneListComponent: LMPagePaneListComponent
  ) {}
}

@Component({
  selector: '[lm-page-pane-list-component]',
  templateUrl: './lm-page-pane-list.component.html',
  styleUrls: ['lm-page-pane-list.component.scss']
})
export class LMPagePaneListComponent implements OnInit {
  // forwardRef to make tests work
  @ViewChild(forwardRef(() => LMListComponent)) lmListComponent;

  @Input() panel: LMPagePanelSpec;
  @Input() pane: LMMultiPagePaneSpec;
  @Input() listSpec: LMListSpec;
  @Input() fromDataView: LMDataView;
  @Input() overrideListTitle: string;
  @Input() pageHasContextForm = false;
  @Input() pageHasTabs = false;

  @Output() paneListContentDisplayed = new EventEmitter<LMContentDisplayedEvent>();

  @Output() paneListRowSelected = new EventEmitter<LMPaneListSelectionEvent>();
  @Output() paneListRowActivated = new EventEmitter<LMPaneRowActivatedEvent>();
  @Output() paneListRowDeactivated = new EventEmitter<LMPaneRowActivatedEvent>();
  @Output() paneContentModified = new EventEmitter<LMListFieldChangedEvent>();
  @Output() panelDataUpdated = new EventEmitter<any>();
  @Output() paneListSendContextMessageEvent = new EventEmitter<LMPaneListSendContextMessageEvent>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();

  private isInitialBusinessMessage = true;
  public listUrlInfo: LMListUrlInfo;

  constructor(private lmUrlInfoBuilderService: LMUrlInfoBuilderService) {}

  ngOnInit(): void {
    this.listUrlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(this.listSpec, { pagePaneSpec: this.pane as LMPagePaneSpec });
  }

  public getPaneSpec(): LMMultiPagePaneSpec {
    return this.pane;
  }

  public isModified(): boolean {
    if (!this.lmListComponent) {
      return false;
    }
    return this.lmListComponent.isModified();
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    if (this.lmListComponent) {
      return this.lmListComponent.refresh(request);
    } else {
      return of (true)
    }
  }

  public save(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (this.lmListComponent && this.isModified()) {
        this.lmListComponent.runSaveAction(AppConstants.STANDARD_SAVE, observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  onListRowSelected(listSelectionEvent: LMListSelectionEvent) {
    const paneListSelectionEvent: LMPaneListSelectionEvent = new LMPaneListSelectionEvent(
      listSelectionEvent.eventSource, listSelectionEvent.selectedRows[0], this);
    this.paneListRowSelected.emit(paneListSelectionEvent);
  }

  onListRowActivated(rowActivationEvent: LMListRowActivationEvent) {
    const paneListSelectionEvent = new LMPaneRowActivatedEvent(rowActivationEvent.eventSource, rowActivationEvent.activatedEvent, this);
    this.paneListRowActivated.emit(paneListSelectionEvent);
  }

  onListRowDeactivated(rowActivationEvent: LMListRowActivationEvent) {
    const paneListSelectionEvent = new LMPaneRowActivatedEvent(rowActivationEvent.eventSource, rowActivationEvent.activatedEvent, this);
    this.paneListRowDeactivated.emit(paneListSelectionEvent);
  }

  onUpdateToolbarStateEvent(event: any) {
    this.updateToolbarStateEvent.emit();
  }

  onSendContextMessageEvent(event: any) {
    let needSendBusinessMessage = true;
    if (this.panel && this.panel.panelType === 'MultiPanePanel') {
      if (!this.pane.isRelativePanel && this.isInitialBusinessMessage) {
        const firstListPane = this.getFirstListPane();
        if (firstListPane && firstListPane.paneId !== this.pane.paneId) {
          needSendBusinessMessage = false;
        }
      }
    }

    if (needSendBusinessMessage) {
      const paneListSendContextMessageEvent = new LMPaneListSendContextMessageEvent(event, this.lmListComponent, this);
      this.paneListSendContextMessageEvent.emit(paneListSendContextMessageEvent);
    }
    this.isInitialBusinessMessage = false;
  }

  onContentModified(event: LMListFieldChangedEvent) {
    this.paneContentModified.emit(event);
  }

  onPanelDataUpdated(event: LMListTransactionEvent) {
    this.panelDataUpdated.emit(event);
  }

  getFirstListPane(): LMMultiPagePaneSpec {
    for (let i = 0; i < this.panel.panes.length; i++) {
      const pane = this.panel.panes[ i ];
      if (pane.paneType === 'ListPanel') {
        return pane;
      }
    }
    return null;
  }

  onListDisplayed(args: any) {
    const event: LMContentDisplayedEvent = {pane: this.pane, args}
    this.paneListContentDisplayed.emit(event);
  }

  savePaneState() {
    if (this.lmListComponent) {
      this.lmListComponent.saveComponentState(true);
    }
  }

  sendBusinessContext(contextMsgs?: any) {
    if (this.lmListComponent) {
      this.lmListComponent.sendBusinessContext(contextMsgs);
    }
  }
}
